import React, { useEffect, useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Btn } from "../../components/UI";
import { toast } from "sonner";
import { X, Loader2, Link2, Copy, RefreshCw, Eye, Megaphone, BarChart2 } from "lucide-react";

const LinkRow = ({ icon: Icon, title, desc, path, token, enabled, busy, onAction, kind, footer }) => {
  const url = token ? `${window.location.origin}/${path}/${token}` : null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy blocked by browser — select the link text and copy manually");
    }
  };
  return (
    <div className="rounded-lg border border-white/10 p-4" data-testid={`link-row-${kind}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-medium text-white inline-flex items-center gap-2"><Icon className="w-4 h-4 text-cyan-300" /> {title}</div>
          <p className="text-xs text-slate-500 mt-1">{desc}</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 ${enabled ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" : "border-white/15 text-slate-500"}`}>
          {enabled ? "Active" : "Off"}
        </span>
      </div>
      {enabled && url && (
        <div className="mt-3 flex items-center gap-2">
          <input readOnly value={url} data-testid={`link-url-${kind}`}
            className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-xs text-slate-300 focus:outline-none" />
          <Btn variant="secondary" onClick={copy} data-testid={`copy-link-${kind}`}><Copy className="w-3.5 h-3.5" /></Btn>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        {!enabled ? (
          <Btn variant="secondary" onClick={() => onAction(kind, "enable")} disabled={busy} data-testid={`enable-link-${kind}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Enable link
          </Btn>
        ) : (
          <>
            <Btn variant="secondary" onClick={() => onAction(kind, "rotate")} disabled={busy} data-testid={`rotate-link-${kind}`}>
              <RefreshCw className="w-3.5 h-3.5" /> Rotate
            </Btn>
            <Btn variant="secondary" onClick={() => onAction(kind, "disable")} disabled={busy} data-testid={`disable-link-${kind}`}>
              Disable
            </Btn>
          </>
        )}
      </div>
      {footer}
    </div>
  );
};

export const ShareModal = ({ candidate, id, onClose, reload }) => {
  const [state, setState] = useState({
    share_token: candidate.share_token, share_enabled: !!candidate.share_enabled,
    volunteer_token: candidate.volunteer_token, volunteer_enabled: !!candidate.volunteer_enabled,
  });
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  const loadStats = () => {
    api.get(`/political-intelligence/candidates/${id}/share-stats`).then(r => setStats(r.data)).catch(() => {});
  };
  useEffect(loadStats, [id]);

  const setAlerts = async (enabled) => {
    try {
      await api.put(`/political-intelligence/candidates/${id}/share-alerts`, { enabled });
      setStats(s => ({ ...s, alerts_enabled: enabled }));
      toast.success(enabled ? "You'll get an email when this link is opened" : "Open alerts turned off");
    } catch (e) { toast.error(errMsg(e, "Could not update alerts")); }
  };

  const act = async (kind, action) => {
    setBusy(true);
    try {
      const r = await api.post(`/political-intelligence/candidates/${id}/links`, { kind, action });
      setState({
        share_token: r.data.share_token, share_enabled: !!r.data.share_enabled,
        volunteer_token: r.data.volunteer_token, volunteer_enabled: !!r.data.volunteer_enabled,
      });
      toast.success(action === "disable" ? "Link disabled" : action === "rotate" ? "New link generated — old one no longer works" : "Link enabled");
      reload();
      loadStats();
    } catch (e) { toast.error(errMsg(e, "Could not update link")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0a0e1a] border border-white/10 rounded-lg max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="share-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">Share this race</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-white" data-testid="share-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4">
          <LinkRow icon={Eye} kind="share" title="Read-only race dashboard" path="race"
            desc="For consultants and donors — polling averages, trendline, issues, opposition and live ads. No spend, strategy or internal data."
            token={state.share_token} enabled={state.share_enabled} busy={busy} onAction={act}
            footer={state.share_enabled && stats ? (
              <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap" data-testid="share-stats">
                <span className="inline-flex items-center gap-1"><BarChart2 className="w-3 h-3 text-cyan-300/70" /> Opened <b className="text-slate-300">{stats.total_views || 0}</b> time{(stats.total_views || 0) === 1 ? "" : "s"}</span>
                <span>{stats.views_7d || 0} in last 7 days</span>
                {stats.last_viewed_at && <span>last viewed {stats.last_viewed_at.slice(0, 16).replace("T", " ")} UTC</span>}
                <label className="inline-flex items-center gap-1.5 cursor-pointer select-none ml-auto" data-testid="share-alerts-toggle">
                  <input type="checkbox" checked={stats.alerts_enabled !== false} onChange={e => setAlerts(e.target.checked)}
                    className="accent-cyan-400" data-testid="share-alerts-checkbox" />
                  Email me on open
                </label>
              </div>
            ) : null} />
          <LinkRow icon={Megaphone} kind="volunteer" title="Volunteer ad-watch portal" path="volunteer"
            desc="A simple mobile page volunteers use to log ads and mailers they spot — sightings land in your Opposition tab instantly."
            token={state.volunteer_token} enabled={state.volunteer_enabled} busy={busy} onAction={act} />
        </div>
        <p className="text-[10px] text-slate-600 mt-4">Anyone with a link can view/submit. Rotate a link to instantly cut off old copies.</p>
      </div>
    </div>
  );
};
